import React, { useContext, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { BrowserProvider } from 'ethers'; // ethers v6
import { AuthContext } from '../../context/authContext';
import styles from './LoginPage.module.css';
import apiRequest from '../../lib/apiRequest';
import MetamaskLogo from '../../components/metaMaskLogo/MetaMaskLogo';
import logger from '../../lib/logger';


const LoginPage = () => {
  const { login } = useContext(AuthContext);
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setSuccessMessage('');

    try {
      const response = await apiRequest.post(
        '/auth/login',
        { email, password },
      );

      if (!response.data.success) {
        setErrorMessage(response.data.message || 'Login failed.');
        return;
      }

      const data = response.data;
      setSuccessMessage(data.message || 'Login successful!');
      login(data.user);

      setTimeout(() => {
        navigate('/');
      }, 1000);

      setEmail('');
      setPassword('');
    } catch (error) {
      logger.error('Error during login:', error);
      setErrorMessage(error.response?.data?.message || 'Login failed.');
    }
  };

  const handleMetamaskLogin = async () => {
    setErrorMessage('');
    setSuccessMessage('');
    try {
      if (!window.ethereum) {
        setErrorMessage("Metamask is not installed. Please install Metamask to use this login method.");
        return;
      }

      // Request accounts from Metamask
      const accounts = await window.ethereum.request({ method: "eth_requestAccounts" });
      const address = accounts[0];

      // Get a challenge from the backend using the wallet address
      const challengeRes = await apiRequest.get(
        `/auth/metamask/challenge?address=${address}`
      );
      const challengeMessage = challengeRes.data.challenge;

      // Create a BrowserProvider and signer with ethers v6
      const provider = new BrowserProvider(window.ethereum);
      const signer = provider.getSigner();
      const signature = await (await signer).signMessage(challengeMessage);
      logger.log("Signature:", signature);

      // Send the address and signature to the backend for login
      const loginRes = await apiRequest.post(
        '/auth/metamask/login',
        { address, signature },
        { withCredentials: true }
      );

      if (!loginRes.data.success) {
        setErrorMessage(loginRes.data.message || "Metamask login failed.");
        return;
      }

      const data = loginRes.data;
      setSuccessMessage(data.message || "Login successful!");
      login(data.user);

      setTimeout(() => {
        navigate('/');
      }, 1000);

    } catch (error) {
      logger.error("Error during Metamask login:", error);
      setErrorMessage(error.response?.data?.message || "Metamask login failed.");
    }
  };

  return (
    <div className={styles.loginContainer}>
      <h2 className={styles.loginHeading}>Sign In</h2>
      <form onSubmit={handleSubmit} className={styles.loginForm}>
        <label htmlFor="email" className={styles.loginLabel}>
          Email
        </label>
        <input
          id="email"
          type="email"
          className={styles.loginInput}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />

        <label htmlFor="password" className={styles.loginLabel}>
          Password
        </label>
        <input
          id="password"
          type="password"
          className={styles.loginInput}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />

        {errorMessage && (
           <div
           className={styles.errorMessage}
           dangerouslySetInnerHTML={{ __html: errorMessage }}
         />
        )}
        {successMessage && (
          <div className={styles.successMessage}>
            {successMessage}
          </div>
        )}

        <button type="submit" className={styles.loginButton}>
          Log In
        </button>
      </form>

      <div className={styles.divider}>or</div>

      <button onClick={handleMetamaskLogin} className={styles.metamaskButton}>
        <MetamaskLogo width={24} height={24} /> MetaMask Login
      </button>

      <div className={styles.additionalLinks}>
        <p>
          Don't have an account?{' '}
          <Link to="/register" className={styles.link}>
            Sign Up
          </Link>
        </p>
        <p>
          <Link to="/forgot-password" className={styles.link}>
            Forgot Password?
          </Link>
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
